import { Link, Translation } from '@ps/web-shared-components';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import type { WidgetFC } from '@swf/core/types/Widget';
import { ExpandablePanel, Icon, Title } from '@swf/sports/components';
import GroupedTemplatePanels from '@swf/sports/components/GroupedTemplatePanels/GroupedTemplatePanels';
import useLiveDataPolling from '@swf/sports/hooks/useLiveDataPolling/useLiveDataPolling';
import useMarketUpdates from '@swf/sports/hooks/useMarketUpdates';
import { getEventTypesInPlayEventIdsMap } from '@swf/sports/utils/getEventTypesInPlayEventIdsMap';
import getEventsFromMarketTemplateData from '@swf/sports/utils/getEventsFromMarketTemplateData';

import { TXTWCL_All_In_Play, TXTWCL_In_Play } from './SportsHomeInPlayWidget.messages.json';
import styles from './SportsHomeInPlayWidget.scss';
import LoadingState from './components/LoadingState/LoadingState';
import Tabs from './components/Tabs/Tabs';
import { sportsHomeInPlayWidgetModel, useSportsHomeInPlayWidget } from './model';

const SportsHomeInPlayWidget: WidgetFC = () => {
  const { widgetDispatch, widgetSelect } = useSportsHomeInPlayWidget();
  const hasErrored = useSelector(widgetSelect.error);
  const hasPreloaded = useSelector(widgetSelect.hasPreloaded);
  const isLoading = useSelector(widgetSelect.isLoading);
  const tabs = useSelector(widgetSelect.tabsWithEventTypeSlugs);
  const events = useSelector(widgetSelect.events);
  const markets = useSelector(widgetSelect.markets);
  const competitions = useSelector(widgetSelect.competitions);
  const eventTypes = useSelector(widgetSelect.eventTypes);
  const marketIds = useSelector(widgetSelect.marketIds);
  const selectedTab = useSelector(widgetSelect.selectedTab);

  const eventTypesEventIdsMap = useMemo(
    () =>
      getEventTypesInPlayEventIdsMap(
        getEventsFromMarketTemplateData(events, markets, competitions, eventTypes)
      ),
    [competitions, eventTypes, events, markets]
  );

  useLiveDataPolling({ eventTypesEventIdsMap });

  useMarketUpdates(marketIds, widgetDispatch.marketsUpdated);

  if (isLoading && !hasPreloaded) {
    return <LoadingState />;
  }

  if (hasErrored || !selectedTab) {
    return null;
  }

  return (
    <ExpandablePanel
      title={
        <Title isNested>
          <Translation {...TXTWCL_In_Play} />
        </Title>
      }
      isNested
    >
      {tabs.length ? <Tabs tabs={tabs} selectedTab={selectedTab} /> : null}
      <GroupedTemplatePanels groupedOn="COMPETITION" groups={selectedTab.groups} isNested />
      <Link
        className={styles.allInPlayButton}
        alias="sportsInPlaySport"
        values={{ eventTypeId: selectedTab.eventTypeId, eventTypeSlug: selectedTab.eventTypeSlug }}
      >
        <Translation {...TXTWCL_All_In_Play} />
        <Icon name="chevron-small-right" size="xl" />
      </Link>
    </ExpandablePanel>
  );
};

SportsHomeInPlayWidget.model = sportsHomeInPlayWidgetModel;

export default withWidgetTestId(SportsHomeInPlayWidget, 'sports-home-in-play', {
  className: styles.wrapper
});
